// ============================================================================
// FSA REPORT PDF PREVIEW MODAL
// ============================================================================
// Per-report PDF preview. Mirrors the FSA template / adjustment-journal
// PDF preview pattern — fetches a server-rendered Blob, renders it in
// an iframe, exposes Download + Open/Print buttons.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';

interface Props {
  isOpen: boolean;
  engagementId: ID | null;
  reportId: ID | null;
  /** Subtitle shown in the modal header — e.g. "AUDIT_REPORT · Independent Auditor's Report". */
  reportLabel?: string | null;
  /**
   * ADR-0024 R-4 — when set, offers "Download Word" (editable DOCX of the same
   * saved report). Sectioned reports only (Director's Report); the value is
   * the download file name.
   */
  wordFileName?: string | null;
  onClose: () => void;
}

const FsaReportPdfPreviewModal: React.FC<Props> = ({
  isOpen,
  engagementId,
  reportId,
  reportLabel,
  wordFileName,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [wordBusy, setWordBusy] = useState(false);

  const handleWord = useCallback(async () => {
    if (!engagementId || !reportId || !wordFileName) return;
    setWordBusy(true);
    try {
      await fsaReportService.downloadDocx(engagementId, reportId, wordFileName);
    } catch {
      notificationService.toast.error('Could not generate the Word document.');
    } finally {
      setWordBusy(false);
    }
  }, [engagementId, reportId, wordFileName]);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (!isOpen || !engagementId || !reportId) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await fsaReportService.getPdfBlob(engagementId, reportId);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load PDF.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, engagementId, reportId]);

  const downloadName = `FSA-Report-${reportId ?? 'unknown'}-${new Date().toISOString().slice(0, 10)}.pdf`;

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups from this site so the PDF can open in a new tab.',
        6000,
      );
    }
  }, [blobUrl]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.print size={16} />
            <h2
              className="modal-title"
              style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Report Preview
              {reportLabel && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                  · {reportLabel}
                </span>
              )}
            </h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div
          className="modal-content"
          style={{
            flex: 1, padding: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            background: 'var(--bg-secondary)',
          }}
        >
          {loading && (
            <div style={spinnerWrapStyle}>
              <div style={spinnerStyle} />
              <span style={spinnerLabelStyle}>Rendering PDF…</span>
            </div>
          )}

          {error && !loading && (
            <div style={errorWrapStyle}>
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not load preview</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{error}</div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="FSA Report Preview PDF"
              style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          {wordFileName && (
            <button
              className="btn btn-secondary"
              onClick={handleWord}
              disabled={wordBusy || !engagementId || !reportId}
              title="Editable Word copy of the same saved report, for finalising"
            >
              <IconSystem.download size={16} />
              {wordBusy ? 'Preparing…' : 'Download Word'}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------

const spinnerWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem',
  height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};
const spinnerLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};

const errorWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '2rem',
  textAlign: 'center',
};

export default FsaReportPdfPreviewModal;
